import { createContext, type ReactNode, useMemo } from 'react';

import type { ActionButtonState } from 'components/ActionButton';
import type { ValidationError } from 'errors';
import { useRequiredContext } from 'hooks';
import type {
	CreateCustomizationRequest,
	CustomizationPlacement,
	Placement,
	StoredFileResponse,
} from 'models/api';
import type { ProductPrice } from 'models/price';
import type { VariantId } from 'models/product';
import type { CustomizationId } from 'types';

import { type ExistingPlacement, useProductPrint } from '../hooks';

interface ProductPrintContextType {
	addPrintPlacement: (printPlacement: CreateCustomizationRequest) => void;
	addPrintPlacementButtonState: ActionButtonState;
	addPrintPlacementErrors: ValidationError | undefined;
	customizationId: CustomizationId | undefined;
	existingPrintPlacements: ExistingPlacement[] | undefined;
	isInitialLoadingProductPrint: boolean;
	placements: Placement[] | undefined;
	print: StoredFileResponse | undefined;
	printPlacements: CustomizationPlacement[] | undefined;
	printUploadButtonState: ActionButtonState;
	printUploadErrors: string[] | undefined;
	productPrintHasError: boolean;
	removePrint: () => void;
	removePrintPlacement: (placementId: string) => void;
	reusePrint: (printId: string | undefined) => void;
	storedPrints: StoredFileResponse[] | undefined;
	totalCost: ProductPrice | undefined;
	uploadPrint: (file: File | undefined) => Promise<void>;
}

export const ProductPrintContext = createContext<
	ProductPrintContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
	isCustomizable: boolean;
	isSellable: boolean;
	variantId: VariantId;
}

export function ProductPrintContextProvider({
	children,
	isCustomizable,
	isSellable,
	variantId,
}: Props) {
	// TODO: the product print hook wraps stuff that could probably live here instead
	const {
		addPrintPlacement,
		addPrintPlacementButtonState,
		addPrintPlacementErrors,
		customizationId,
		existingPrintPlacements,
		hasError: productPrintHasError,
		isInitialLoading: isInitialLoadingProductPrint,
		placements,
		print,
		printPlacements,
		printUploadButtonState,
		printUploadErrors,
		removePrint,
		removePrintPlacement,
		reusePrint,
		storedPrints,
		totalCost,
		uploadPrint,
	} = useProductPrint({
		id: variantId,
		isActive: isCustomizable && isSellable,
	});
	const value = useMemo(
		() => ({
			addPrintPlacement,
			addPrintPlacementButtonState,
			addPrintPlacementErrors,
			customizationId,
			existingPrintPlacements,
			productPrintHasError,
			isInitialLoadingProductPrint,
			placements,
			print,
			printPlacements,
			printUploadButtonState,
			printUploadErrors,
			removePrint,
			removePrintPlacement,
			reusePrint,
			storedPrints,
			totalCost,
			uploadPrint,
		}),
		[
			addPrintPlacement,
			addPrintPlacementButtonState,
			addPrintPlacementErrors,
			customizationId,
			existingPrintPlacements,
			isInitialLoadingProductPrint,
			placements,
			print,
			printPlacements,
			printUploadButtonState,
			printUploadErrors,
			productPrintHasError,
			removePrint,
			removePrintPlacement,
			reusePrint,
			storedPrints,
			totalCost,
			uploadPrint,
		],
	);

	return <ProductPrintContext value={value}>{children}</ProductPrintContext>;
}
ProductPrintContextProvider.displayName = 'ProductPrintContextProvider';

export function useProductPrintContext() {
	return useRequiredContext(ProductPrintContext, 'ProductPrintContext');
}
